import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@mela/design-system';
import { reportBoundary } from '../lib/errorReporter';

// Catches React render/lifecycle errors so a bad page doesn't show a blank
// white screen. Reports the error via /errors and offers the user a way out
// (back to home + reload).

interface Props {
  children: ReactNode;
}

interface State {
  err: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { err: null };

  static getDerivedStateFromError(err: Error): State {
    return { err };
  }

  componentDidCatch(err: Error, info: ErrorInfo) {
    reportBoundary(err.message ?? 'Unknown error', (err.stack ?? '') + '\n---\n' + (info.componentStack ?? ''));
  }

  reset = () => {
    this.setState({ err: null });
  };

  render() {
    if (!this.state.err) return this.props.children;
    return (
      <div className="container-mela py-24 max-w-[640px] text-center">
        <div className="font-mono text-[11px] tracking-wide uppercase text-vermilion-700 font-semibold mb-3">
          Something broke
        </div>
        <h1 className="font-display text-[32px] font-bold text-ink-900 -tracking-[0.02em] mb-4">
          We tripped on this page.
        </h1>
        <p className="text-[15px] text-ink-700 leading-relaxed mb-8 max-w-[44ch] mx-auto">
          The error has been reported to the founding team. Reloading usually fixes a one-off
          glitch; if it persists, we’ll have a fix shortly.
        </p>
        <div className="flex gap-3 justify-center flex-wrap">
          <Button variant="primary" size="lg" onClick={() => window.location.reload()}>
            Reload page
          </Button>
          <Link
            to="/"
            onClick={this.reset}
            className="inline-flex items-center justify-center h-12 px-6 rounded-md border border-ink-300 hover:border-ink-500 text-ink-800 text-[15px] font-semibold transition-colors duration-fast"
          >
            Back to home
          </Link>
        </div>
      </div>
    );
  }
}
